import {
  Add as AddIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  ChevronRight as ChevronRightIcon,
  Delete as DeleteIcon,
  DeleteOutline as DeleteOutlineIcon,
  DragIndicator as DragIndicatorIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  Collapse,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';

import { blockDisallowedFtsKey, sanitizeEmojis } from '@/constants/regex';
import { Evaluation } from '@/models/evaluations';

export const NotChip = ({
  active,
  onClick,
  disabled
}: {
  active?: boolean;
  onClick: () => void;
  disabled?: boolean;
}) => (
  <Chip
    id="evaluation-keyword-negate"
    label="NOT"
    size="small"
    clickable={!disabled}
    onClick={disabled ? undefined : onClick}
    sx={{
      fontWeight: 700,
      fontSize: 11,
      ...(active
        ? { bgcolor: '#d32f2f', color: '#fff' }
        : {
            bgcolor: 'transparent',
            border: '1px solid #ccc',
            color: 'text.secondary'
          })
    }}
  />
);

export const ConnectorPill = ({
  operator,
  onToggle,
  disabled
}: {
  operator: Evaluation.Operator;
  onToggle: () => void;
  disabled?: boolean;
}) => (
  <Chip
    id="evaluation-keyword-connector"
    label={operator === 'AND' ? 'And' : 'Or'}
    size="small"
    clickable={!disabled}
    onClick={disabled ? undefined : onToggle}
    sx={
      operator === 'AND'
        ? { bgcolor: '#5e35b1', color: '#fff' }
        : { bgcolor: '#ede7f6', color: '#5e35b1' }
    }
  />
);

export const OperatorToggle = ({
  operator,
  onSet,
  disabled
}: {
  operator: Evaluation.Operator | null;
  onSet: (op: Evaluation.Operator) => void;
  disabled?: boolean;
}) => (
  <ButtonGroup size="small" disabled={disabled} id="evaluation-operator-toggle">
    <Button
      variant={operator === 'OR' ? 'contained' : 'outlined'}
      onClick={() => onSet('OR')}
    >
      ANY
    </Button>
    <Button
      variant={operator === 'AND' ? 'contained' : 'outlined'}
      onClick={() => onSet('AND')}
    >
      ALL
    </Button>
  </ButtonGroup>
);

export const KeywordPill = ({
  label,
  isNegated,
  onToggle,
  onDelete,
  disabled,
  draggable,
  onDragStart,
  onDragEnd,
  isDragging
}: {
  label: string;
  isNegated?: boolean;
  onToggle?: () => void;
  onDelete?: () => void;
  disabled?: boolean;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragEnd?: () => void;
  isDragging?: boolean;
}) => {
  const canDrag = !!draggable && !disabled;

  return (
    <Stack
      direction="row"
      draggable={canDrag}
      onDragStart={canDrag ? onDragStart : undefined}
      onDragEnd={canDrag ? onDragEnd : undefined}
      sx={{
        border: '1px solid #ddd',
        borderRadius: 1,
        overflow: 'hidden',
        opacity: isDragging ? 0.4 : 1,
        cursor: canDrag ? 'grab' : undefined
      }}
    >
      {canDrag && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 0.5,
            bgcolor: '#fff',
            color: '#161D288A'
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>
      )}
      {onToggle && (
        <Box
          id="evaluation-keyword-toggle"
          onClick={disabled ? undefined : onToggle}
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 1,
            bgcolor: isNegated ? '#d32f2f' : '#2e7d32',
            cursor: disabled ? 'default' : 'pointer'
          }}
        >
          {isNegated ? (
            <BlockIcon fontSize="small" sx={{ color: '#fff' }} />
          ) : (
            <CheckCircleIcon fontSize="small" sx={{ color: '#fff' }} />
          )}
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          py: 0.5,
          bgcolor: '#fff'
        }}
      >
        <Typography variant="body2">{label}</Typography>
      </Box>
      {onDelete && (
        <IconButton
          size="small"
          disabled={disabled}
          onClick={onDelete}
          sx={{ borderRadius: 0, bgcolor: '#fff' }}
        >
          <DeleteIcon fontSize="small" sx={{ color: '#161D288A' }} />
        </IconButton>
      )}
    </Stack>
  );
};

export const AddButtonsRow = ({
  onAddKeyword,
  onAddGroup,
  onAddLevel,
  disabled,
  showInput,
  inputValue,
  onInputChange,
  onInputKeyDown,
  onInputBlur
}: {
  onAddKeyword: () => void;
  onAddGroup: () => void;
  onAddLevel: () => void;
  disabled?: boolean;
  showInput?: boolean;
  inputValue?: string;
  onInputChange?: (value: string) => void;
  onInputKeyDown?: (key: string) => void;
  onInputBlur?: () => void;
}) => (
  <Stack
    direction="row"
    spacing={1}
    alignItems="center"
    sx={{ my: 1, flexWrap: 'wrap' }}
  >
    <Box sx={{ position: 'relative' }}>
      <Button
        id="evaluation-keyword"
        size="small"
        variant="outlined"
        startIcon={<AddIcon />}
        disabled={disabled}
        onClick={onAddKeyword}
        sx={{
          color: 'text.primary',
          borderRadius: 1,
          borderColor: 'text.primary'
        }}
      >
        Keyword
      </Button>
      {showInput && (
        <TextField
          id="evaluation-input"
          size="small"
          autoFocus
          placeholder="keyword"
          value={inputValue}
          onChange={(e) => onInputChange?.(sanitizeEmojis(e.target.value))}
          onKeyDown={(e) => {
            blockDisallowedFtsKey(e as React.KeyboardEvent<HTMLInputElement>);
            onInputKeyDown?.(e.key);
          }}
          onBlur={onInputBlur}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            mt: 0.5,
            width: 220,
            backgroundColor: 'background.paper'
          }}
        />
      )}
    </Box>
    <Button
      id="evaluation-keyword-group"
      size="small"
      variant="outlined"
      startIcon={<AddIcon />}
      disabled={disabled}
      onClick={onAddGroup}
      sx={{
        color: 'text.primary',
        borderRadius: 1,
        borderColor: 'text.primary'
      }}
    >
      Keyword group
    </Button>
    <Button
      id="evaluation-keyword-level"
      size="small"
      variant="outlined"
      startIcon={<AddIcon />}
      disabled={disabled}
      onClick={onAddLevel}
      sx={{
        color: 'text.primary',
        borderRadius: 1,
        borderColor: 'text.primary'
      }}
    >
      Logical level
    </Button>
  </Stack>
);

export const GroupBox = ({
  accentSx,
  isNegated,
  isOpen,
  onToggleExpand,
  onToggleNegate,
  subtitle,
  badge,
  operator,
  onSetOperator,
  onRemove,
  disabled,
  children,
  draggable,
  onDragStart,
  onDragEnd,
  isDragging,
  onHoverDragEnter,
  operatorDisabled
}: {
  accentSx: object;
  isNegated?: boolean;
  isOpen: boolean;
  onToggleExpand: () => void;
  onToggleNegate: () => void;
  subtitle: React.ReactNode;
  badge: React.ReactNode;
  operator: Evaluation.Operator | null;
  onSetOperator: (op: Evaluation.Operator) => void;
  onRemove: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragEnd?: () => void;
  isDragging?: boolean;
  onHoverDragEnter?: (e: React.DragEvent<HTMLDivElement>) => void;
  operatorDisabled: boolean;
}) => {
  const canDrag = !!draggable && !disabled;

  return (
    <Box
      id="group-box"
      draggable={canDrag}
      onDragStart={canDrag ? onDragStart : undefined}
      onDragEnd={canDrag ? onDragEnd : undefined}
      onDragEnter={onHoverDragEnter}
      sx={{
        borderRadius: 1,
        ...accentSx,
        p: 1,
        opacity: isDragging ? 0.4 : 1,
        cursor: canDrag ? 'grab' : undefined
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        {canDrag && (
          <DragIndicatorIcon fontSize="small" sx={{ color: '#161D288A' }} />
        )}
        <IconButton
          id="evaluation-keyword-expand"
          size="small"
          onClick={onToggleExpand}
        >
          {isOpen ? (
            <ExpandMoreIcon fontSize="small" />
          ) : (
            <ChevronRightIcon fontSize="small" />
          )}
        </IconButton>
        <NotChip
          active={isNegated}
          onClick={onToggleNegate}
          disabled={disabled}
        />
        <Box sx={{ flexGrow: 1 }}>{subtitle}</Box>
        {badge}
        <OperatorToggle
          operator={operator}
          onSet={onSetOperator}
          disabled={disabled || operatorDisabled}
        />
        <IconButton size="small" disabled={disabled} onClick={onRemove}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Collapse in={isOpen}>
        <Box sx={{ pl: 4, pt: 1 }}>{children}</Box>
      </Collapse>
    </Box>
  );
};

export const DropZone = ({
  isDragActive,
  isHovered,
  onDragOver,
  onDrop,
  onDragLeave
}: {
  isDragActive: boolean;
  isHovered: boolean;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: () => void;
}) => {
  let width = 0;
  let backgroundColor = 'transparent';

  if (isHovered || isDragActive) {
    width = 64;
    backgroundColor = isHovered ? '#5e35b1' : '#ede7f6';
  }

  return (
    <Box
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragLeave={onDragLeave}
      sx={{
        alignSelf: 'stretch',
        width,
        minHeight: 28,
        borderRadius: 1,
        bgcolor: backgroundColor,
        transition: 'width 0.12s ease, background-color 0.12s ease'
      }}
    />
  );
};

export type KeywordGroupData = {
  id: number;
  title: string;
  keywords: string[];
};

export const KeywordGroupCard = ({
  group,
  selected,
  onToggle
}: {
  group: KeywordGroupData;
  selected: boolean;
  onToggle: () => void;
}) => {
  const visibleKeywords = group.keywords.slice(0, 5);
  const hiddenKeywords = group.keywords.slice(5);
  const overflowCount = group.keywords.length - visibleKeywords.length;

  return (
    <Box
      onClick={onToggle}
      sx={{
        position: 'relative',
        cursor: 'pointer',
        border: selected ? '1px solid #66bb6a' : '1px solid #ddd',
        borderRadius: 1,
        p: 2,
        bgcolor: '#fff'
      }}
    >
      {selected && (
        <CheckCircleIcon
          sx={{
            position: 'absolute',
            top: -10,
            left: -10,
            color: '#43a047',
            bgcolor: '#fff',
            borderRadius: '50%',
            fontSize: 22
          }}
        />
      )}
      <Typography sx={{ fontWeight: 700, mb: 1 }}>{group.title}</Typography>
      <Stack direction="row" flexWrap="wrap" gap={0.75}>
        {visibleKeywords.map((kw) => (
          <Chip key={kw} label={kw} size="small" sx={{ bgcolor: '#f0f0f0' }} />
        ))}
        {overflowCount > 0 && (
          <Tooltip title={hiddenKeywords.join(', ')} arrow>
            <Chip
              label={`+${overflowCount}`}
              size="small"
              sx={{ bgcolor: '#1a1a2e', color: '#fff', borderRadius: '50%' }}
            />
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
};
